/**
 * Step 1 of the bulk upload: the file picker (one CSV, XLS or XLSX file), tips, a link to the
 * brand's Excel template, and an expandable example of the file layout
 * (`ExampleFileStructureTable/ExampleFileStructureTable.tsx`). The file is parsed by
 * `BulkUploadModal.tsx` → `readFile`, which sets `validFile`.
 *
 * The template is a static asset:
 * `<NEXT_PUBLIC_URI_STATIC>/static-assets/documents/templates/<language>/<file>.xlsx`, where
 * `<file>` is `RyersonBulkUploadTemplate` or `STSBulkUploadTemplate` by `NEXT_PUBLIC_COMPANY`, with
 * `_shoppingList` appended for list uploads. The files live in
 * `web-static-assets/application/documents/templates/<language>/`; a renamed or missing template
 * breaks this link. There is no `STSBulkUploadTemplate_shoppingList.xlsx` there, and no name for
 * the planned Central Steel brand (`Company.CENTRAL_STEEL`) in `allTemplateFileNames`.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { Divider } from "@components/CustomerDashboard/Helpers/StyledComponents";
import { Flex } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { BulkUploadStepProps } from "./BulkUploadModalDesktop";
import { Link } from "@ryerson/frontend.navigation";
import { useApplication } from "@ryerson/frontend.application";
import { FileInput, FileInputTypes } from "@ryerson/frontend.form-controls";
import styled from "@emotion/styled";
import { Expander, ExpansionBox } from "@ryerson/frontend.expansion";
import { BulkUploadType } from "./BulkUpload.model";
import ExampleFileStructureTable from "./ExampleFileStructureTable/ExampleFileStructureTable";
import { Company } from "@ryerson/common.enums";

const FileSelectionContainer = styled(Flex)`
	margin-top: 59px;
	margin-bottom: 42px;
`;

const BulletContainer = styled(Flex)`
	margin-top: 10px;
`;

const Bullet = styled(Flex)`
	margin-left: -4px;
	margin-top: 10px;
`;

/** Step 1: choose the file and get the template; see the file header. */
export const FileSelection: React.FC<BulkUploadStepProps> = ({
	content,
	bulkUploadFiles = [],
	setBulkUploadFiles = () => {},
	submittedFileInput: submitted = false,
	validFile,
	bulkUploadType,
}) => {
	const { theme } = useTheme();
	const { label, link, header, error } = theme.colors.primary;

	const {
		localization: { language },
	} = useApplication();

	const onChange = (files: File[]) => {
		setBulkUploadFiles(files);
	};

	const allTemplateFileNames = {
		[Company.RYERSON]: "RyersonBulkUploadTemplate",
		[Company.SOUTHERN_TOOL_STEEL]: "STSBulkUploadTemplate",
	} as { [key in Company]: string };

	// `NEXT_PUBLIC_COMPANY` is fixed at build time per brand. An unset value gives `ryerson`, which
	// matches no template file; a company missing from the map gives `undefined`.
	const brandedFileName = process.env.NEXT_PUBLIC_COMPANY
		? allTemplateFileNames[process.env.NEXT_PUBLIC_COMPANY as Company]
		: "ryerson";

	return (
		<>
			<FileSelectionContainer direction="column">
				<Typography size="lg" css={{ marginBottom: 38 }}>
					{content.toolHelpUpload}
				</Typography>
				<Flex
					css={{ height: bulkUploadFiles.length === 0 ? "175px" : "120px" }}
					direction="column"
				>
					<FileInput
						language={language}
						submitted={submitted}
						required={true}
						onChange={onChange}
						allowedTypes={[FileInputTypes.CSV, FileInputTypes.XLS, FileInputTypes.XLSX]}
						maxFiles={1}
						title={content.uploadFile}
						dataTestId="bulk-upload"
					/>
				</Flex>
				{!validFile && bulkUploadFiles.length > 0 && (
					<Typography color={error}>{content.invalidContent}</Typography>
				)}
				<BulletContainer direction="column">
					<Bullet alignItems="center">
						<Icon icon={"dot-circle"} size="xs" />
						<Typography color={header}>{content.tipCsvFile}</Typography>
					</Bullet>
					<Bullet alignItems="center">
						<Icon icon={"dot-circle"} size="xs" />
						<Typography color={header}>{content.tipNoQuantities}</Typography>
					</Bullet>
					<Bullet alignItems="center">
						<Icon icon={"dot-circle"} size="xs" />
						<Typography color={header} css={{ marginRight: "6px" }}>
							{content.tipConvenience}
						</Typography>
						<Link
							next={false}
							target="_blank"
							to={
								(process.env.NEXT_PUBLIC_URI_STATIC ?? "") +
								`/static-assets/documents/templates/${language}/${brandedFileName}${
									bulkUploadType === BulkUploadType.shoppingList
										? "_shoppingList"
										: ""
								}.xlsx`
							}
						>
							<Icon
								icon={"file-xls"}
								size="sm"
								color={link}
								css={{ cursor: "pointer" }}
							/>
						</Link>
						<Link
							css={{ marginLeft: "6px" }}
							next={false}
							target="_blank"
							to={
								(process.env.NEXT_PUBLIC_URI_STATIC ?? "") +
								`/static-assets/documents/templates/${language}/${brandedFileName}${
									bulkUploadType === BulkUploadType.shoppingList
										? "_shoppingList"
										: ""
								}.xlsx`
							}
						>
							{content.tipXLSTemplate}
						</Link>
					</Bullet>
				</BulletContainer>
			</FileSelectionContainer>
			<Divider theme={theme} />
			<ExpansionBox
				clickableHeader={true}
				defaultOpen={false}
				header={
					<Typography variant="h4" color={label}>
						{content.exampleXLS}
					</Typography>
				}
			>
				<Expander>
					<ExampleFileStructureTable {...{ bulkUploadType }} />
				</Expander>
			</ExpansionBox>
		</>
	);
};
